import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle, Package, ArrowLeft, XCircle } from "lucide-react";
import axios from "axios";
import { usePrice } from "../../hooks/usePrice";
import { Container } from "../../components/Container/Container";

import styled from "styled-components";

const SuccessSection = styled.section`
    padding: 3rem 0;
    text-align: center;

    h1, h3 {
        font-weight: 700;
        color: #1e293b;
    }
`;

const OrderCard = styled.section`
    background: white;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    margin-bottom: 2rem;
`;

const OrderItem = styled.li`
    padding: 1rem;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
`;

const TotalRow = styled.div`
    padding: 1.5rem;
    background: #f8fafc;
    font-weight: 700;
    display: flex;
    justify-content: space-between;
`;

const InfoCard = styled.section`
    background: white;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    padding: 2rem;
    text-align: center;
`;

const HomeButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 1rem 2rem;
  background: #3b82f6;
  color: white;
  border-radius: 12px;
  text-decoration: none;
`;

export default function PaymentSuccess() {
    const { countPrice } = usePrice();
    const { state } = useLocation(); // здесь orderSummary
    const [params] = useSearchParams();

    const invoiceId = params.get("invoiceId");

    const [loading, setLoading] = useState(true);
    const [payment, setPayment] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!invoiceId) {
            setError("invoiceId отсутствует в URL");
            setLoading(false);
            return;
        }

        const token = sessionStorage.getItem("accessToken");

        if (!token) {
            setError("Нет авторизации");
            setLoading(false);
            return;
        }

        axios
            .get(
                `${process.env.REACT_APP_API_BASE_URL}/user/status/${invoiceId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            )
            .then(({ data }) => {
                setPayment(data);
            })
            .catch((err) => {
                console.error(err);
                setError("Ошибка получения статуса оплаты");
            })
            .finally(() => setLoading(false));
    }, [invoiceId]);

    if (loading) return <Container><h3>Загрузка...</h3></Container>;

    if (error)
        return (
            <Container>
                <InfoCard>
                    <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h3>Ошибка</h3>
                    <p>{error}</p>
                    <HomeButton to="/">На главную</HomeButton>
                </InfoCard>
            </Container>
        );

    const isSuccess = payment?.status === "CHARGE";

    if (isSuccess)
        return (
            <Container>
                <SuccessSection>
                    <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                    <h1>Оплата прошла успешно!</h1>
                    <p>Спасибо за вашу покупку!</p>
                </SuccessSection>

                <OrderCard>
                    <div className="header">
                        <h2 style={{ padding: "1rem" }}>Состав заказа</h2>
                    </div>

                    <div className="content">
                        {state?.orderSummary?.items?.length > 0 ? (
                            <>
                                <ul>
                                    {state.orderSummary.items.map((item) => (
                                        <OrderItem key={item.id}>
                                            <div>{item.name} × {item.quantity}</div>
                                            <div>{countPrice(item.price)}</div>
                                        </OrderItem>
                                    ))}
                                </ul>

                                <TotalRow>
                                    <span>Итого</span>
                                    <span>{countPrice(state.orderSummary.total)}</span>
                                </TotalRow>
                            </>
                        ) : (
                            <p style={{ padding: "1rem" }}>В заказе нет товаров</p>
                        )}
                    </div>
                </OrderCard>

                {/* ----- Информация об оплате ----- */}
                <InfoCard>
                    <h3>Информация об оплате</h3>
                    <p>Сумма: <b>{payment.amount} {payment.currency}</b></p>
                    <p>Карта: <b>{payment.cardMask} ({payment.cardType})</b></p>
                    <p>Номер транзакции: <b>{payment.invoiceId}</b></p>
                    <p>Дата: {payment.transactionDate}</p>
                </InfoCard>

                <div style={{ textAlign: "center", marginBottom: "3rem" }}>
                    <HomeButton to="/">На главную</HomeButton>
                </div>
            </Container>
        );

    return (
        <Container>
            <InfoCard>
                <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
                <h3>Платёж не прошёл</h3>
                <p>Статус: {payment?.status}</p>
                <p>Сумма: {payment?.amount} {payment?.currency}</p>

                <HomeButton to="/shopping-cart" style={{ marginTop: "1rem" }}>
                    Вернуться в корзину
                </HomeButton>
            </InfoCard>
        </Container>
    );
}
